import { Navigate, useLocation } from "react-router-dom";
import { useAuthStatus } from "./session";
import { AuthSplash } from "./AuthSplash";
import { LoginScreen } from "./LoginScreen";
import { ForwardAfterAuth } from "./ForwardAfterAuth";
import { CHECK_EMAIL_PATH, LOGIN_PATH } from "./paths";
import { AUTH_ENABLED } from "./config";

/* Permanent redirect from /auth (the URL studio's invite emails point at) to
 * the canonical /login, preserving the query string (e.g. ?redirect=… /
 * ?invitation_id=…) so invite deeplinks and old bookmarks keep working. */
export function InviteLoginRedirect() {
  const { search } = useLocation();
  return <Navigate to={`${LOGIN_PATH}${search}`} replace />;
}

/* The /login route. Renders the login screen for unauthenticated users, and
 * once authenticated forwards them on. Invite emails link to
 * /auth?invitation_id=… (the URL is fixed by the identity service) which
 * redirects here, so an invitation_id in the query is handled directly:
 * forward to the dedicated /invite/:id
 * accept page. Otherwise honor the `redirect` param set by RequireAuth (or
 * home). When auth is disabled there is no login — redirect to the dashboard. */
export function LoginRoute() {
  if (!AUTH_ENABLED) return <Navigate to="/" replace />;

  return <LoginGate />;
}

function LoginGate() {
  const { status } = useAuthStatus();
  const location = useLocation();

  if (status === "loading") return <AuthSplash />;

  // Signed in but unverified — park on /check-email, carrying any invite/redirect
  // query so it survives the detour (CheckEmailRoute forwards there once the
  // email is verified). Without this, an invited-but-unverified user would lose
  // the invitation on sign-in.
  if (status === "unverified") {
    return <Navigate to={`${CHECK_EMAIL_PATH}${location.search}`} replace />;
  }

  if (status === "authenticated") {
    return <ForwardAfterAuth search={location.search} />;
  }

  return <LoginScreen />;
}
